<template>
  <div>
    <div class="mb-3">
      <label class="form-label">{{
        t("common.counterparty_picker.company_label")
      }}</label>
      <GlobalCompanySearchDropdown
        :modelValue="model.companyName"
        :withLabel="false"
        :withDescription="false"
        :disabled="disabled"
        @select="handleCompanySelected"
        @update:modelValue="handleCompanyNameChange"
      />
    </div>
    <div class="mb-3">
      <label for="counterpartyAccountNumber" class="form-label">{{
        t("common.counterparty_picker.account_label")
      }}</label>
      <div class="input-group">
        <input
          type="text"
          id="counterpartyAccountNumber"
          class="form-control"
          :class="{ 'is-invalid': hasAccountError }"
          v-model="localAccountNumber"
          :disabled="disabled"
        />
        <span
          v-if="derivedCountryDisplay"
          class="input-group-text bg-light text-muted small"
        >
          {{ derivedCountryDisplay }}
        </span>
      </div>
      <div v-if="hasAccountError" class="text-danger small mt-1">
        <span v-for="error of v$.accountNumber.$errors" :key="error.$uid">
          {{ error.$message }}
        </span>
        <span v-if="authorityAccountError">{{ authorityAccountError }}</span>
      </div>
      <div
        v-else-if="showUnrecognizedWarning && !hasMismatch"
        class="form-text text-warning-emphasis"
      >
        {{ t("common.counterparty_picker.account_unrecognized_warning") }}
      </div>
      <EecsNameMismatchWarning
        v-model:hasMismatch="hasMismatch"
        :companyName="model.companyName"
        :accountNumber="model.accountNumber"
        :showUnknownHint="!hasAccountError && !showUnrecognizedWarning"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import EecsNameMismatchWarning from "@/components/common/EecsNameMismatchWarning.vue";
import GlobalCompanySearchDropdown from "@/components/common/GlobalCompanySearchDropdown.vue";
import { useValidators } from "@/composables/useValidators";
import type { PickedCompany } from "@/types";
import { formatCountryWithFlag } from "@/utils/countries";
import {
  AUTHORITY_ACCOUNT_MESSAGE_KEYS,
  isValidRegistryAccountNumber,
  registryCountryForAccount,
  type RegistrationAuthority,
} from "@/utils/registryAccounts";
import { useVuelidate } from "@vuelidate/core";
import { computed, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";

export interface CounterpartyModel {
  companyName: string;
  accountNumber: string;
}

const model = defineModel<CounterpartyModel>({ required: true });

const props = defineProps<{
  disabled?: boolean;
  registrationAuthority?: RegistrationAuthority | null;
}>();

const emit = defineEmits<{
  "select:company": [company: PickedCompany];
}>();

const { t } = useI18n();
const validators = useValidators(t);

// Flag to prevent race condition between company selection and manual typing
const isSelectingCompany = ref(false);

const rules = computed(() => ({
  accountNumber: {
    validFormat: validators.acceptableAccountNumber,
  },
}));

const v$ = useVuelidate(rules, model, { $autoDirty: true });

// NOTE: deliberately outside vuelidate: the authority can change while the
// field does not (certificate selection deriving a different authority), and
// the mismatch must surface immediately rather than after the next touch.
const authorityAccountError = computed(() => {
  const authority = props.registrationAuthority;
  if (!authority || !model.value.accountNumber) return "";
  return isValidRegistryAccountNumber(model.value.accountNumber, authority)
    ? ""
    : t(AUTHORITY_ACCOUNT_MESSAGE_KEYS[authority]);
});

const derivedCountryCode = computed(() =>
  registryCountryForAccount(
    model.value.accountNumber,
    props.registrationAuthority
  )
);

const derivedCountryDisplay = computed(() => {
  const code = derivedCountryCode.value;
  return code ? formatCountryWithFlag(code) : "";
});

// Keyed on country derivability, not format recognition: an EECS-formatted
// value with an unknown AIB prefix (e.g. 99X1234567) is a recognized format
// (no relaxed flag needed on save) but still warns that no country/badge can
// be derived from it.
const showUnrecognizedWarning = computed(
  () =>
    !!model.value.accountNumber &&
    !v$.value.accountNumber.$invalid &&
    derivedCountryCode.value === null
);

const hasAccountError = computed(
  () => v$.value.accountNumber.$error || !!authorityAccountError.value
);

const hasMismatch = ref(false);

const localAccountNumber = computed({
  get: () => model.value.accountNumber,
  set: (value: string) => {
    model.value = { ...model.value, accountNumber: value };
  },
});

const handleCompanySelected = async (company: PickedCompany) => {
  isSelectingCompany.value = true;

  model.value = {
    ...model.value,
    companyName: company.name,
    // Preserve a manually entered account when the picked company has none.
    accountNumber: company.account_eecs || model.value.accountNumber,
  };

  emit("select:company", company);

  await nextTick();
  isSelectingCompany.value = false;
};

const handleCompanyNameChange = async (companyName: string) => {
  if (isSelectingCompany.value) {
    await nextTick();
    if (isSelectingCompany.value) {
      return;
    }
  }

  model.value = { ...model.value, companyName };
};

defineExpose({
  isValid: () => !v$.value.$invalid && !authorityAccountError.value,
  touchAndValidate: () => {
    v$.value.$touch();
    return !v$.value.$invalid && !authorityAccountError.value;
  },
});
</script>

<style scoped lang="scss"></style>
